// ============================================================
// TERROR TCG — PROCEDURAL PACK ART
// ------------------------------------------------------------
// Generates a unique, deterministic foil-wrapper for every pack
// from its accentColor + code. No image assets, no CDN, no 404s.
// If caP'n later hand-draws wrappers, swap PackArt for an <img>.
//
// This is the visual centerpiece of the boutique: each pack is a
// distinct wrapped object with its own sigil, foil sheen, and
// tier-colored seal. Cosmetic only — zero gameplay logic here.
// ============================================================

// ── Deterministic tiny PRNG so a given pack always looks the same ──
function seededRand(seedStr) {
  let h = 1779033703 ^ seedStr.length;
  for (let i = 0; i < seedStr.length; i++) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let state = h >>> 0;
  return () => {
    state = Math.imul(state ^ (state >>> 16), 2246822507);
    state = Math.imul(state ^ (state >>> 13), 3266489909);
    state ^= state >>> 16;
    return (state >>> 0) / 4294967296;
  };
}

// Lighten/darken a hex color by amt (-1..1)
function shade(hex, amt) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  if (amt >= 0) {
    r = Math.round(r + (255 - r) * amt);
    g = Math.round(g + (255 - g) * amt);
    b = Math.round(b + (255 - b) * amt);
  } else {
    const a = 1 + amt;
    r = Math.round(r * a); g = Math.round(g * a); b = Math.round(b * a);
  }
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// ── Sigil shapes — one is chosen deterministically per pack ──
// Each returns an SVG path/element string, drawn centered in a
// 100×100 viewBox. Strokes only, so it reads on any wrapper color.
const SIGILS = {
  // Eclipse — concentric broken rings
  eclipse: (c) => `
    <circle cx="50" cy="50" r="26" fill="none" stroke="${c}" stroke-width="2" stroke-dasharray="3 5" opacity="0.9"/>
    <circle cx="50" cy="50" r="18" fill="none" stroke="${c}" stroke-width="1.5" opacity="0.7"/>
    <circle cx="50" cy="50" r="9" fill="${c}" opacity="0.85"/>
    <circle cx="50" cy="50" r="9" fill="none" stroke="#000" stroke-width="1" opacity="0.5"/>`,
  // Star — radiant burst
  star: (c) => {
    const pts = [];
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
      const r = i % 2 === 0 ? 28 : 12;
      pts.push(`${50 + Math.cos(a) * r},${50 + Math.sin(a) * r}`);
    }
    return `<polygon points="${pts.join(" ")}" fill="${c}" opacity="0.85" stroke="${shade(c, 0.4)}" stroke-width="1"/>
            <circle cx="50" cy="50" r="5" fill="#000" opacity="0.4"/>`;
  },
  // Gear — clockwork teeth
  gear: (c) => {
    let teeth = "";
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * 360;
      teeth += `<rect x="48" y="14" width="4" height="8" fill="${c}" opacity="0.9" transform="rotate(${a} 50 50)"/>`;
    }
    return `${teeth}
            <circle cx="50" cy="50" r="20" fill="none" stroke="${c}" stroke-width="3" opacity="0.85"/>
            <circle cx="50" cy="50" r="8" fill="none" stroke="${c}" stroke-width="2" opacity="0.7"/>`;
  },
  // Diamond — stacked rhombi (dragon/scale energy)
  diamond: (c) => `
    <polygon points="50,16 78,50 50,84 22,50" fill="none" stroke="${c}" stroke-width="2.5" opacity="0.9"/>
    <polygon points="50,28 66,50 50,72 34,50" fill="${c}" opacity="0.5"/>
    <polygon points="50,38 58,50 50,62 42,50" fill="${c}" opacity="0.85"/>`,
  // Hex — council / authority
  hex: (c) => {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
      pts.push(`${50 + Math.cos(a) * 28},${50 + Math.sin(a) * 28}`);
    }
    const inner = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
      inner.push(`${50 + Math.cos(a) * 15},${50 + Math.sin(a) * 15}`);
    }
    return `<polygon points="${pts.join(" ")}" fill="none" stroke="${c}" stroke-width="2.5" opacity="0.9"/>
            <polygon points="${inner.join(" ")}" fill="${c}" opacity="0.55"/>`;
  },
  // Eye — phantom / prophecy
  eye: (c) => `
    <path d="M22,50 Q50,26 78,50 Q50,74 22,50 Z" fill="none" stroke="${c}" stroke-width="2.5" opacity="0.9"/>
    <circle cx="50" cy="50" r="11" fill="${c}" opacity="0.6"/>
    <circle cx="50" cy="50" r="5" fill="#000" opacity="0.7"/>
    <circle cx="53" cy="47" r="2" fill="#fff" opacity="0.8"/>`,
  // Triskele — chaos / randomizer
  triskele: (c) => {
    let arms = "";
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * 360;
      arms += `<path d="M50,50 Q${50 + 4},${50 - 22} ${50 + 18},${50 - 24}" fill="none" stroke="${c}" stroke-width="3" stroke-linecap="round" opacity="0.85" transform="rotate(${a} 50 50)"/>`;
    }
    return `${arms}<circle cx="50" cy="50" r="6" fill="${c}" opacity="0.9"/>`;
  },
  // Sigil-of-bind — contract / tome
  bind: (c) => `
    <rect x="28" y="28" width="44" height="44" fill="none" stroke="${c}" stroke-width="2" opacity="0.85" transform="rotate(45 50 50)"/>
    <line x1="50" y1="22" x2="50" y2="78" stroke="${c}" stroke-width="1.5" opacity="0.7"/>
    <line x1="22" y1="50" x2="78" y2="50" stroke="${c}" stroke-width="1.5" opacity="0.7"/>
    <circle cx="50" cy="50" r="7" fill="${c}" opacity="0.8"/>`,
};

const SIGIL_KEYS = Object.keys(SIGILS);

// Map a pack code prefix → preferred sigil (falls back to seeded pick)
const CODE_SIGIL = {
  VE: "eclipse", SA: "star", CD: "gear", DW: "diamond", SC: "hex",
  IR: "triskele", UW: "triskele", SP: "star", PH: "eye", TM: "gear",
  DR: "diamond", SG: "diamond", RT: "bind", NL: "eclipse", CB: "bind",
  ASM: "star", DS: "eclipse", HC: "triskele", AA: "hex", PO: "eye", RS: "triskele",
};

// ─────────────────────────────────────────────────────────────
// PackArt — the wrapped booster pack as pure SVG
//   props: accentColor, code, w, h, foil (bool), tierGlow (color)
// ─────────────────────────────────────────────────────────────
export function PackArt({ accentColor = "#d4a84b", code = "TER", w = 120, h = 168, foil = false, premium = false }) {
  const rand = seededRand(code + accentColor);
  const baseKey = (code || "").split("-")[0];
  const sigilKey = CODE_SIGIL[baseKey] || SIGIL_KEYS[Math.floor(rand() * SIGIL_KEYS.length)];
  const sigil = SIGILS[sigilKey](accentColor);

  const dark = shade(accentColor, -0.78);
  const darker = shade(accentColor, -0.88);
  const mid = shade(accentColor, -0.5);
  const light = shade(accentColor, 0.35);
  const uid = `pk_${code.replace(/[^a-zA-Z0-9]/g, "")}_${Math.floor(rand() * 1e6)}`;

  // scatter a few foil flecks deterministically
  const flecks = Array.from({ length: 7 }, () => ({
    x: 10 + rand() * 80, y: 10 + rand() * 80, r: 0.6 + rand() * 1.6, o: 0.3 + rand() * 0.5,
  }));

  // ── LIVING WRAPPER (Magnum Fable) — deterministic per pack ──
  // The sheen now TRAVELS (SMIL), the sigil breathes, the flecks
  // twinkle. Each pack gets its own timing off the same seeded PRNG,
  // so no two wrappers on the shelf pulse in sync — the case shimmers.
  const sheenDur = (4.2 + rand() * 2.6).toFixed(2);   // one sweep pass
  const sigilDur = (5.5 + rand() * 3).toFixed(2);     // sigil breath
  const sheenDelay = (rand() * 3).toFixed(2);

  return (
    <svg viewBox="0 0 120 168" width={w} height={h} xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", filter: premium ? `drop-shadow(0 0 10px ${accentColor}66)` : "none" }}>
      <defs>
        <clipPath id={`${uid}_clip`}>
          <rect x="3" y="3" width="114" height="162" rx="12" />
        </clipPath>
        <linearGradient id={`${uid}_body`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={mid} />
          <stop offset="45%" stopColor={dark} />
          <stop offset="100%" stopColor={darker} />
        </linearGradient>
        <linearGradient id={`${uid}_sheen`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity={foil ? 0.3 : 0.16} />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={`${uid}_glow`} cx="50%" cy="38%" r="60%">
          <stop offset="0%" stopColor={accentColor} stopOpacity="0.45" />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
        </radialGradient>
        {foil && (
          <linearGradient id={`${uid}_foil`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff3ea5" stopOpacity="0.35">
              <animate attributeName="stop-color"
                values="#ff3ea5;#ffd84a;#3affc4;#3aa8ff;#b46aff;#ff3ea5"
                dur="6s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#3aa8ff" stopOpacity="0.35">
              <animate attributeName="stop-color"
                values="#3aa8ff;#b46aff;#ff3ea5;#ffd84a;#3affc4;#3aa8ff"
                dur="6s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        )}
      </defs>

      {/* Body */}
      <rect x="3" y="3" width="114" height="162" rx="12" fill={`url(#${uid}_body)`}
        stroke={accentColor} strokeWidth="2" />

      {/* Iridescent foil wash (boutique packs only) */}
      {foil && (
        <rect x="3" y="3" width="114" height="162" rx="12" fill={`url(#${uid}_foil)`}
          style={{ mixBlendMode: "color-dodge" }} />
      )}

      {/* Top glow halo behind sigil */}
      <rect x="3" y="3" width="114" height="162" rx="12" fill={`url(#${uid}_glow)`} />

      {/* Crimp line near top (the "tear here" seam) */}
      <line x1="10" y1="30" x2="110" y2="30" stroke={accentColor} strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
      <line x1="10" y1="33" x2="110" y2="33" stroke={darker} strokeWidth="1" opacity="0.6" />

      {/* Sigil — centered emblem, breathing (raw SVG injected so the
          shape strings render; the <animate> targets the outer group). */}
      <g opacity="0.9">
        <animate attributeName="opacity" values="0.72;1;0.72" dur={`${sigilDur}s`} repeatCount="indefinite" />
        <g dangerouslySetInnerHTML={{ __html: `<svg x="10" y="40" width="100" height="100" viewBox="0 0 100 100">${sigil}</svg>` }} />
      </g>

      {/* Foil flecks — twinkling, each on its own beat */}
      {flecks.map((f, i) => (
        <circle key={i} cx={f.x} cy={f.y + 30} r={f.r} fill={light} opacity={f.o}>
          <animate attributeName="opacity"
            values={`${(f.o * 0.35).toFixed(2)};${Math.min(1, f.o + 0.3).toFixed(2)};${(f.o * 0.35).toFixed(2)}`}
            dur={`${(2.4 + (i % 4) * 0.9).toFixed(1)}s`}
            begin={`${(i * 0.55).toFixed(2)}s`}
            repeatCount="indefinite" />
        </circle>
      ))}

      {/* Traveling sheen sweep — the caselight sliding across the
          wrapper. Clipped to the pack body; skewed like a real glint. */}
      <g clipPath={`url(#${uid}_clip)`}>
        <g transform="skewX(-16)">
          <rect x="0" y="-30" width="40" height="230" fill={`url(#${uid}_sheen)`}>
            <animateTransform attributeName="transform" type="translate"
              values="-70 0; 190 0; 190 0" keyTimes="0; 0.42; 1"
              dur={`${sheenDur}s`} begin={`${sheenDelay}s`} repeatCount="indefinite" />
          </rect>
        </g>
      </g>

      {/* Code plate at bottom */}
      <rect x="28" y="140" width="64" height="18" rx="4" fill="#000" opacity="0.55" />
      <rect x="28" y="140" width="64" height="18" rx="4" fill="none" stroke={accentColor} strokeWidth="0.8" opacity="0.7" />
      <text x="60" y="152.5" textAnchor="middle" fontFamily="'Cinzel', serif" fontWeight="900"
        fontSize="10" letterSpacing="2" fill={light}>{code}</text>

      {/* Corner ticks */}
      {[[10, 10], [110, 10], [10, 158], [110, 158]].map(([cx, cy], i) => (
        <g key={i} opacity="0.6">
          <circle cx={cx} cy={cy} r="2" fill={accentColor} />
        </g>
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// TierGem — a small faceted gem in a holo tier's color.
// Used in the Foil Vault and tier legend. Pure SVG, animated sheen.
// ─────────────────────────────────────────────────────────────
export function TierGem({ color = "#cccccc", size = 28, glow = true }) {
  const uid = `gem_${color.replace("#", "")}`;
  const lite = shade(color, 0.5);
  const drk = shade(color, -0.4);
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} xmlns="http://www.w3.org/2000/svg"
      style={{ filter: glow ? `drop-shadow(0 0 4px ${color})` : "none" }}>
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={lite} />
          <stop offset="50%" stopColor={color} />
          <stop offset="100%" stopColor={drk} />
        </linearGradient>
      </defs>
      <polygon points="20,3 34,15 27,36 13,36 6,15" fill={`url(#${uid})`} stroke={lite} strokeWidth="1" />
      <polygon points="20,3 27,15 20,20 13,15" fill="#ffffff" opacity="0.35" />
      <polygon points="13,15 20,20 13,36 6,15" fill="#000" opacity="0.18" />
      <line x1="20" y1="3" x2="20" y2="20" stroke="#ffffff" strokeWidth="0.6" opacity="0.4" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// HOLO TIER REGISTRY — single source of truth for tier visuals.
// Mirrors the tier strings used across progression / CollectionViewer.
//
// ── THE KHAO RENAME (July 25, 2026 — caP'n's ruling) ──────────
// IE_HOLO_DOCTRINE §11 flagged Void and Shadow as too close both
// visually and lexically and slated one for phase-out. caP'n's call:
// keep the VOID RECIPE (the Maelstrom — nothing about the artwork
// changes, not one hex value) and retire the NAME. It is now **Khao**.
// With a K. Shadow leaves the shelf entirely until a new, genuinely
// distinct Shadow variant is designed.
//
// ── WHY BOTH OLD KEYS SURVIVE BELOW ───────────────────────────
// `holo_void` and `holo_shadow` are written into PLAYER COLLECTIONS in
// Firebase (`holoTiers: { holo_void: 2 }`). Deleting the keys would
// orphan every foil anyone already owns. So:
//   • holo_void   → LEGACY ALIAS. Resolves, and DISPLAYS AS KHAO, so a
//     copy pulled last week and one pulled tomorrow read identically.
//   • holo_shadow → RETIRED. Kept resolvable so the record survives,
//     but out of TIER_ORDER, so nothing can ever mint it again.
// Neither appears in TIER_ORDER — that list is what is MINTABLE and
// what the UI walks. Read paths walk TIER_ORDER_ALL instead.
//
// ⚠ DOCTRINE §5 (Migration Law) — a retired tier renders BASE + flag.
// It is NEVER approximated with another tier's recipe. Shadow copies
// therefore render unfoiled until the new Shadow ships. That is the
// correct behaviour, not a bug.
// ─────────────────────────────────────────────────────────────
export const HOLO_TIER_INFO = {
  holo_silver:   { label: "Silver",   glyph: "◈", color: "#cccccc", blurb: "Quicksilver. Liquid metal settling — the base foil, alive." },
  holo_gold:     { label: "Gold",     glyph: "✦", color: "#ffd700", blurb: "Molten. Poured gold from the seam; flecks twinkle in the surface." },
  holo_mythic:   { label: "Mythic",   glyph: "◆", color: "#ff66cc", blurb: "Prism. Light split into its colors, recombining into white. The classic chase." },
  holo_khao:     { label: "Khao",     glyph: "◈", color: "#9333ea", blurb: "The Maelstrom. Roiling smoke around a throat that eats the light." },
  holo_solrend:  { label: "Solrend",  glyph: "◆", color: "#ff2266", blurb: "The Rend. Sunfire tears the card open, flares, and seals. Descent beyond rank." },
  holo_aeternus: { label: "A'eterni", glyph: "✦", color: "#f0f0ff", blurb: "Halo. The heartbeat of eternity — rings of light that never stop." },
  holo_depths:   { label: "Depths",   glyph: "◆", color: "#00aaff", blurb: "Caustics. The light-web of a surface far overhead. The Gatekeeper's signature." },
  holo_jynxi:    { label: "J'ynxi",   glyph: "✦", color: "#2ee66b", blurb: "Trickster's emerald. Friend or faux — the green that grins back." },

  // ── VROWN — Sir H'osh Vrown's own, Minerium One's signature tier ──
  // caP'n, 2026-08-22: "the very first Minerium belongs to Sir Hash
  // Vrown [H'osh Vrown]... mimic hash browns going white gold to
  // brown without being intrusive." Every Mythic card earned inside
  // Minerium One wears this — never a recolor of Gold or Solrend; the
  // griddle motion (HoloFoil.js) is its own signature, and no future
  // Minerium reuses it. See TERROR_STORY_SPINE.md, "THE DIVISION."
  holo_vrown:    { label: "Vrown",    glyph: "◆", color: "#c17a2e", blurb: "The Griddle. White-gold catches first, then the sear presses through — and the shreds under it brown as the heat arrives. Sir H'osh Vrown's own, and only Minerium One's." },
  holo_gauntlet: { label: "Gauntlet", glyph: "\u2726", color: "#b9c2cc", blurb: "The Grip. Four plates close on the card and clench — the only foil in the game that contracts. Earned in the Gauntlet, bought nowhere else." },

  // ── LEGACY / RETIRED — resolvable, never mintable ──
  holo_void:     { label: "Khao",     glyph: "◈", color: "#9333ea", blurb: "The Maelstrom. Roiling smoke around a throat that eats the light.", legacyOf: "holo_khao" },
  holo_shadow:   { label: "Shadow",   glyph: "◇", color: "#9966ff", blurb: "Eclipse. Withdrawn from circulation — a new Shadow is being cut.", retired: true },
};

// MINTABLE + DISPLAY order. Eight live tiers. Anything not in this list
// cannot be pulled, sold, or offered — that is the whole point of it.
export const TIER_ORDER = [
  "holo_silver", "holo_gold", "holo_mythic", "holo_khao",
  "holo_solrend", "holo_jynxi", "holo_aeternus", "holo_depths",
  "holo_vrown",
  "holo_gauntlet",
];

// READ order. Every lookup that asks "which foil does this copy wear?"
// walks THIS list, so a collection holding a legacy key still resolves.
// (Shatterfield's getHoloTier, DeckBuilder, CollectionViewer, Codex.)
export const TIER_ORDER_ALL = [...TIER_ORDER, "holo_void", "holo_shadow"];

// The one place a legacy key becomes its modern name. Anything that
// GROUPS, COUNTS, or COMPARES tiers should canonicalise first, or a
// player with one pre-rename and one post-rename copy will look like
// they own two different foils.
export const HOLO_TIER_ALIAS = { holo_void: "holo_khao" };
export const canonTier = (t) => HOLO_TIER_ALIAS[t] || t;
export const isMintableTier = (t) => TIER_ORDER.includes(canonTier(t));

// ─────────────────────────────────────────────────────────────
// SIGNATURE TIER — the ONE special holo a pack can yield.
// 2.0 rule: a pack's aura color == the unique holo inside it, so
// there's zero guesswork on the shelf. Silver/Gold can still appear
// in any pack as the "common" foils; the signature is the special one.
//
// Resolution order:
//   1. pack.signatureTier (explicit, if set in data)
//   2. first entry of pack.holoTiers that isn't silver/gold
//   3. first entry of pack.holoTiers
//   4. null  → no special tier (standard pack, aura uses accentColor)
// ─────────────────────────────────────────────────────────────
const COMMON_FOILS = new Set(["holo_silver", "holo_gold"]);

export function getPackSignatureTier(pack) {
  if (!pack) return null;
  if (pack.signatureTier && HOLO_TIER_INFO[pack.signatureTier]) return pack.signatureTier;
  const tiers = Array.isArray(pack.holoTiers) ? pack.holoTiers : [];
  const special = tiers.find(t => !COMMON_FOILS.has(t));
  if (special && HOLO_TIER_INFO[special]) return special;
  if (tiers[0] && HOLO_TIER_INFO[tiers[0]]) return tiers[0];
  return null;
}

// The aura/glow color for a pack on the shelf.
// Signature tier color when present, else the pack's own accent.
export function getPackAuraColor(pack) {
  const sig = getPackSignatureTier(pack);
  if (sig) return HOLO_TIER_INFO[sig].color;
  return pack?.accentColor || "#d4a84b";
}
